import {Link} from 'react-router-dom';
import {ArrowLeft, Gift} from 'lucide-react';
import {Button} from '@/components/ui/button';
import voucherImage from "@/assets/voucher.jpg";
import Footer from '@/components/sections/Footer';

export default function Voucher() {
    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Header */}
            <div className="bg-primary/5">
                <div className="max-w-4xl mx-auto px-4 py-12">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6"
                    >
                        <ArrowLeft className="h-4 w-4"/>
                        Zpět na Swimmie
                    </Link>

                    <div className="flex items-start gap-4">
                        <div className="mt-1">
                            <Gift className="h-8 w-8 text-primary"/>
                        </div>
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                                Dárkový voucher Swimmie
                            </h1>
                            <p className="text-lg text-muted-foreground max-w-2xl">
                                Daruj (nebo si dopřej) motivaci konečně naplnit plavecké cíle –
                                od prvního souvislého kilometru až po přípravu na triatlon.
                                Voucher odemyká přístup k&nbsp;tréninkovým plánům, které ti pomohou
                                naplánovat, odplavat a hlavně
                                <span className="font-semibold text-primary"> dokončit</span> tvé
                                osobní výzvy.
                            </p>
                        </div>
                    </div>

                    {/* CTA buttons */}
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Button asChild size="lg" variant="outline" className="flex-1">
                            <a href="https://buy.stripe.com/7sY28qeJx84M3qdcW50gw00" target="_blank" rel="noreferrer">
                                Zakoupit voucher pro iOS
                            </a>
                        </Button>

                        <Button asChild variant="outline" size="lg" className="flex-1">
                            <a href="https://buy.stripe.com/14A4gy1WLgBi9OBcW50gw01" target="_blank" rel="noreferrer">
                                Zakoupit voucher pro Android
                            </a>
                        </Button>
                    </div>

                    {/* Exaltation section */}
                    <div className="mt-12 space-y-4 text-base text-foreground/90 text-justify">
                        <p>
                            Voucher na Swimmie je víc než jen kód do aplikace. Je to malý
                            závazek sám sobě: letos opravdu naplavu, co jsem si slíbil. Každý
                            tréninkový plán vede krok za krokem k cíli – ať už je to první
                            kilometr v kuse, pravidelné plavání třikrát týdně nebo příprava na
                            závod.
                        </p>
                        <p>
                            Ať už voucher věnuješ kamarádovi, partnerovi nebo sám sobě,
                            vždycky říkáš: „Věřím ti, že na to máš.“ Swimmie ti připomene, kdy
                            jít do bazénu, co plavat a jak si udržet motivaci i ve dnech, kdy
                            se ti nechce.
                        </p>
                        <p>
                            Přidej k voucheru pár vlastních slov, vytiskni ho nebo přepošli
                            e-mailem – z obyčejného dárku se stane konkrétní plán, jak si
                            někdo splní svůj plavecký sen.
                        </p>
                    </div>
                </div>

                <div className="m-6 flex justify-center">
                    <img
                        src={voucherImage}
                        alt="Ukázka dárkového voucheru Swimmie"
                        className="rounded-xl shadow-lg max-w-full md:max-w-2xl"
                    />
                </div>


                {/* Wave separator */}
                <div className="w-full overflow-hidden leading-none mt-16">
                    <svg
                        viewBox="0 0 1440 80"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-full h-16"
                        preserveAspectRatio="none"
                    >
                        <path
                            d="M0,48 C240,80 480,80 720,64 C960,48 1200,16 1440,32 L1440,80 L0,80 Z"
                            fill="rgb(71, 107, 252)"
                        />
                    </svg>
                </div>
            </div>

            {/* Terms placeholder */}
            <div className="flex-1 bg-primary text-white">
                <div className="max-w-4xl mx-auto px-4 py-12 space-y-6">
                    <h2 className="text-2xl md:text-3xl font-semibold text-white">
                        Podrobné podmínky využití voucheru
                    </h2>
                    <div className="space-y-4 text-sm md:text-base text-white leading-relaxed text-justify [&_a]:text-white [&_a]:underline [&_a]:decoration-white">
                        <p className="text-white">
                            Uplatněním voucheru získáte 90 dnů předplatného Swimmie Pro. <strong>Uplatněte jej nejpozději do 23.
                            května 2026</strong>, jinak bez náhrady propadá. Aktivujete jej snadno přímo v aplikaci Swimmie (na
                            platební stránce pod odkazem „Mám slevový kód“) nebo v App Store/Google Play. Pro snadnou
                            aktivaci je třeba, aby váš Swimmie účet používal stejný e-mail jako váš App Store/Play Store
                            účet. Pokud tomu tak není a potřebujete voucher aktivovat pod jiným e-mailem, napište nám
                            prosím na <a className="text-white visited:text-white hover:text-white underline" href="mailto:info@swimmie.cz">info@swimmie.cz</a>.
                            Za zakoupené vouchery nevracíme peníze.
                        </p>
                        <p className="text-white">
                            Prosím pozor, po 90 dnech se Vám předplatné automaticky obnoví na 30 denní a my s tím
                            bohužel (skrz nastavení AppStore/Google Play) nemůžeme nic udělat, proto nám přijde fér dát
                            Vám to na vědomí. Obnovení předplatného po vypršení voucheru si samozřejmě můžete zrušit
                            v App Storu nebo Google Play.
                        </p>
                        <p className="text-white">
                            Všechny dotazy prosím směřujte na <a className="text-white visited:text-white hover:text-white underline" href="mailto:info@swimmie.cz">info@swimmie.cz</a>.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer/>
        </div>
    );
}
